import type { Profile, SessionRecord } from "../data/schema";
import { Screen } from "./ui";

export function HistoryScreen({
  profile,
  sessions,
  onBack,
}: {
  profile: Profile;
  sessions: SessionRecord[];
  onBack: () => void;
}) {
  const completed = sessions
    .filter((s) => s.status === "completed")
    .sort((a, b) => (a.date < b.date ? 1 : -1));

  // Benchmark trends from locally logged results.
  const trends = new Map<string, { label: string; points: { date: string; value: number }[] }>();
  for (const s of [...completed].reverse()) {
    for (const r of s.results ?? []) {
      const drill = s.plan.drills.find((d) => d.drill.id === r.drillId)?.drill;
      const label = drill?.benchmark?.label ?? r.metric;
      if (!trends.has(r.metric)) trends.set(r.metric, { label, points: [] });
      trends.get(r.metric)!.points.push({ date: s.date, value: r.value });
    }
  }

  return (
    <Screen
      title={`${profile.name}'s history`}
      right={
        <button onClick={onBack} className="text-lg font-semibold text-[var(--color-pitch)]">
          Back
        </button>
      }
    >
      {[...trends.values()].length > 0 && (
        <div className="mb-6">
          <h2 className="h-display mb-2 text-2xl">Progress</h2>
          {[...trends.values()].map((t) => {
            const max = Math.max(...t.points.map((p) => p.value), 1);
            const first = t.points[0]?.value;
            const last = t.points[t.points.length - 1]?.value;
            return (
              <div key={t.label} className="card mb-3 p-4">
                <div className="flex items-baseline justify-between">
                  <span className="font-semibold">{t.label}</span>
                  <span className="h-display text-3xl text-[var(--color-pitch-dark)]">
                    {last}
                    {first !== undefined && last !== undefined && last !== first && (
                      <span className={`ml-2 text-lg ${last > first ? "text-green-600" : "text-red-600"}`}>
                        {last > first ? "▲" : "▼"} {Math.abs(last - first)}
                      </span>
                    )}
                  </span>
                </div>
                <div className="mt-3 flex h-16 items-end gap-1">
                  {t.points.slice(-16).map((p, i) => (
                    <div
                      key={i}
                      title={`${p.date}: ${p.value}`}
                      className="flex-1 rounded-t bg-[var(--color-pitch-light)]"
                      style={{ height: `${(p.value / max) * 100}%` }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <h2 className="h-display mb-2 text-2xl">Sessions</h2>
      {completed.length === 0 ? (
        <p className="text-neutral-500">No sessions logged yet. Go train!</p>
      ) : (
        <div className="space-y-2">
          {completed.map((s) => (
            <div key={s.id} className="card p-4">
              <div className="flex items-center justify-between">
                <span className="font-semibold">{s.date}</span>
                <span className="text-neutral-500">
                  {s.durationMin} min
                  {s.perceivedDifficulty ? ` · effort ${s.perceivedDifficulty}/5` : ""}
                </span>
              </div>
              <p className="mt-1 text-sm text-neutral-600">
                {s.plan.drills.map((d) => d.drill.name).join(" · ")}
              </p>
              {s.notes && <p className="mt-1 text-sm italic text-neutral-500">{s.notes}</p>}
            </div>
          ))}
        </div>
      )}
    </Screen>
  );
}
